import { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { OsLink } from "@/components/ui/os-layout";
interface NavLinkProps {
  to: string;
  icon?: React.ReactNode;
  label: string;
  badge?: number | string;
  isActive?: boolean;
  collapsed?: boolean;
  rightIcon?: React.ReactNode;
  isGroup?: boolean;
  onIconClick?: () => void;
  canCollapse?: boolean;
  themeBaseTextColor?: "light" | "dark";
}

export function NavLink({
  to,
  icon,
  label,
  badge,
  isActive,
  collapsed,
  rightIcon,
  onIconClick,
  isGroup,
  canCollapse = true,
  themeBaseTextColor = "light",
}: NavLinkProps) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [scrollState, setScrollState] = useState<
    "idle" | "scrolling" | "returning"
  >("idle");
  const [needsScrolling, setNeedsScrolling] = useState(false);
  const [scrollDistance, setScrollDistance] = useState(0);
  // Track if we've already checked for scrolling
  const hasCheckedScrollingRef = useRef(false);

  // Check if text needs scrolling (if it's truncated)
  useEffect(() => {
    // Only check for scrolling when not collapsed
    if (!collapsed && labelRef.current && containerRef.current) {
      const element = labelRef.current;
      const container = containerRef.current;

      // Only check for scrolling on initial load or when label changes
      // Skip checking when just transitioning from collapsed to expanded
      if (
        !hasCheckedScrollingRef.current ||
        label.length !== element.textContent?.length
      ) {
        // Only consider scrolling needed if the text width is significantly larger than container
        // Adding a small buffer (5px) to prevent unnecessary scrolling for minor differences
        const needsScroll = element.scrollWidth > container.clientWidth + 5;
        setNeedsScrolling(needsScroll);

        if (needsScroll) {
          // Calculate exactly how far we need to scroll - just enough to see the full text
          // Subtract container width from text width to get the exact scroll distance
          const exactScrollDistance =
            element.scrollWidth - container.clientWidth;

          // Add a small buffer (5px) to ensure the last character is fully visible
          setScrollDistance(exactScrollDistance + 5);
        }

        // Mark that we've checked for scrolling
        hasCheckedScrollingRef.current = true;
      }
    } else if (collapsed) {
      // When collapsed, we don't need scrolling
      setScrollState("idle");
      setIsHovering(false);
    }
  }, [label, collapsed]);

  // Reset scroll state when component unmounts
  useEffect(() => {
    return () => {
      hasCheckedScrollingRef.current = false;
    };
  }, []);

  // Handle hover with delay
  useEffect(() => {
    // Don't do any scrolling when collapsed
    if (collapsed) return;

    let initialDelayTimeout: NodeJS.Timeout;
    let scrollingTimeout: NodeJS.Timeout;
    let returningTimeout: NodeJS.Timeout;

    if (isHovering && needsScrolling) {
      // Initial delay before starting to scroll
      initialDelayTimeout = setTimeout(() => {
        setScrollState("scrolling");

        // After scrolling completes, stay at the end position
        const scrollDuration = Math.max(label.length * 100, 2000);
        scrollingTimeout = setTimeout(() => {
          // We'll stay in the scrolled position until user unhovers
          // No need to set returning state while still hovering
        }, scrollDuration);
      }, 500); // Initial delay
    } else {
      // When no longer hovering, smoothly return to initial state
      if (scrollState !== "idle") {
        setScrollState("returning");

        // Reset to idle after returning animation completes
        returningTimeout = setTimeout(() => {
          setScrollState("idle");
        }, 1000); // Time for return transition
      }
    }

    return () => {
      clearTimeout(initialDelayTimeout);
      clearTimeout(scrollingTimeout);
      clearTimeout(returningTimeout);
    };
  }, [isHovering, needsScrolling, label.length, scrollState, collapsed]);

  // Calculate transform based on scroll state
  const getTransform = () => {
    switch (scrollState) {
      case "scrolling":
        // Only scroll exactly as far as needed
        return `translateX(-${scrollDistance}px)`;
      case "returning":
      case "idle":
        return "translateX(0)";
      default:
        return "translateX(0)";
    }
  };

  // Calculate transition based on scroll state
  const getTransition = () => {
    switch (scrollState) {
      case "scrolling":
        // Smooth transition to the scrolled position
        return `transform ${Math.max(label.length * 100, 2000)}ms ease-in-out`;
      case "returning":
        // Smooth transition back to the start
        return "transform 1000ms ease-out";
      default:
        // Quick transition for other cases
        return "transform 300ms ease-out";
    }
  };

  const getDynamicTextClasses = () => {
    // For non-active links, respect the theme's base text color preference
    // Sidebar is dark, so 'light' means light text, 'dark' would mean even darker (less likely for sidebar)
    // but if sidebar could be light, then 'dark' from theme would be dark text.
    // Assuming NavLink is always on a dark background (the sidebar)
    return themeBaseTextColor === "dark"
      ? "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200" // If theme wants dark text, use a subdued dark
      : "text-white hover:text-white/80 dark:text-white dark:hover:text-white/80"; // Default to light text
  };

  return (
    <OsLink
      to={to}
      preventScrollReset={true}
      className={`relative flex items-center gap-3 px-2.5 min-h-[32px] rounded-lg group ${
        isActive ? "bg-slate-900/30 text-white" : getDynamicTextClasses()
      } ${collapsed ? "justify-center pr-0 pl-0" : ""}`}
      title={label}
    >
      {icon && (
        <div
          className={`flex-shrink-0 relative w-5 self-center flex items-center justify-center icon-clickable`}
          onClick={
            collapsed
              ? undefined
              : onIconClick && canCollapse
              ? (e) => {
                  e.preventDefault();
                  onIconClick();
                }
              : (e) => e.preventDefault()
          }
        >
          <div
            className={`absolute text-lg ${
              isActive ? "text-white" : getDynamicTextClasses()
            }`}
          >
            {icon}
          </div>
        </div>
      )}

      <div
        ref={containerRef}
        className={`flex-grow min-w-0 overflow-hidden transition-opacity duration-400 ${
          collapsed ? "opacity-0 hidden" : "opacity-100"
        }`}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{ position: "relative" }}
      >
        <span
          ref={labelRef}
          className={`relative top-[2px] font-medium inline-block whitespace-nowrap ${
            isActive ? "text-white" : getDynamicTextClasses()
          } ${
            isGroup
              ? collapsed
                ? "text-sm font-mono text-[11px] uppercase tracking-widest"
                : "text-sm font-mono text-[11px] uppercase tracking-widest"
              : ""
          }`}
          style={{
            transform: needsScrolling ? getTransform() : "translateX(0)",
            transition: needsScrolling ? getTransition() : "none",
            textOverflow: scrollState === "idle" ? "ellipsis" : "clip",
            overflow: "hidden",
            maxWidth: scrollState === "idle" ? "100%" : "none",
            display: "inline-block",
            width: scrollState === "idle" ? "100%" : "auto",
          }}
        >
          {label}
        </span>
      </div>
      {!collapsed && <div className="flex-shrink-0">{rightIcon}</div>}
      {isGroup && !collapsed && (
        <div
          className={`top-[-1px] transition-all duration-200 ease-in-out -left-2 group-hover:left-0 group-hover:opacity-100 opacity-0 relative font-mono text-xs ${
            isActive
              ? "text-white"
              : "text-white/60 dark:text-white/60 hover:text-white dark:hover:text-white"
          }`}
        >
          <FontAwesomeIcon icon={faArrowRight} />
        </div>
      )}

      {!collapsed && badge != null && (
        <span
          className={`px-1.5 py-0 text-xs rounded-full ${
            isActive
              ? "bg-indigo-100 text-indigo-500 dark:bg-indigo-400/20 dark:text-indigo-400"
              : "bg-gray-100 text-gray-600 group-hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:group-hover:bg-gray-700"
          }`}
        >
          {badge}
        </span>
      )}
      {collapsed && badge != null && (
        <div className="absolute -top-1 -right-1">
          <span
            className={`px-1.5 py-0.5 text-xs rounded-full ${
              isActive
                ? "bg-indigo-200 text-indigo-500 dark:bg-indigo-400/20 dark:text-indigo-400"
                : "bg-gray-200 text-gray-600 group-hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:group-hover:bg-gray-700"
            }`}
          >
            {badge}
          </span>
        </div>
      )}
    </OsLink>
  );
}
